import Button from '@mui/material/Button';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { useTransactionId } from '@/hooks/useTransactionId';
import { useTransactionNotification } from '@/hooks/useTransactionNotification';
import { useTranslator } from '@/hooks/useTranslator';
import { IntegratorOperation, StatusType, TransactionNotification } from '@/types/transaction';

interface Props {
  onClose(): void;
}

export const DepositPending: React.FC<Props> = (props: Props): React.ReactElement => {
  const { onClose } = props;

  const tr = useTranslator();
  const transactionId = useTransactionId();
  const notifications = useTransactionNotification(transactionId);
  const navigate = useNavigate();

  const [operation, setOperation] = React.useState<string>('');

  React.useEffect((): void => {
    const completed = notifications[notifications.length - 1];
    if (completed?.status === StatusType.SUCCEEDED) {
      // Go to next step
      navigate('/home/deposit/completed', { replace: true });
      return;
    }

    const current = notifications
      .slice()
      .reverse()
      .find(
        (notification: TransactionNotification): boolean =>
          notification.status === StatusType.SUCCEEDED,
      );

    if (current) {
      const { operation } = current;

      setOperation(friendlyOperationNames[operation] ?? '');
    }
  }, [navigate, notifications]);

  const description = React.useMemo(
    (): string =>
      `We received your ${operation} and are processing your deposit. Deposits can take up to 4 minutes to complete. You can track the status if your deposit in Activity.`,
    [operation],
  );

  return (
    <ApplicationModal
      title={tr('WE_ARE_DEPOSITING_YOUR_FUNDS')}
      description={description}
      onClose={onClose}>
      <Button variant="dark" onClick={onClose} fullWidth={true}>
        {tr('CLOSE')}
      </Button>
    </ApplicationModal>
  );
};

const friendlyOperationNames: Partial<{ [key in IntegratorOperation]: string }> = {
  [IntegratorOperation.ON_RAMP_DEPOSIT_RECEIVED]: 'on ramp deposit',
  [IntegratorOperation.PIX_DEPOSIT_RECEIVED]: 'PIX deposit',
  [IntegratorOperation.WIRE_DEPOSIT_RECEIVED]: 'wire deposit',
  [IntegratorOperation.SPEI_DEPOSIT_RECEIVED]: 'SPEI deposit',
  [IntegratorOperation.CRYPTO_DEPOSIT_RECEIVED]: 'crypto deposit',
};
